import React, { useState } from 'react';
import { Eye, Edit3, Trash2, BookOpen, Check, X } from 'lucide-react';
import './TestPreview.scss';

const TestPreview = ({ onCancel }) => {
  // --- BASE STATE MAINTAINED ---
  const [tests, setTests] = useState([
    { 
      id: 101, section: 'Quantitative Aptitude', type: 'CAT', class: '8', div: 'B', date: '31/03/2026', time: '10:00', duration: '40 min', topic: 'GIT',
      question: "Your team follows the personal-branch workflow described in the material. Which sequence of actions most closely follows the recommended practice?",
      options: [
        { letter: 'A', text: "Push your local branch to the shared repository under your personal branch name, open a merge request targeting master." },
        { letter: 'B', text: "Checkout master locally, merge your local 'dev-feature' into master, then push master directly." }
      ]
    },
    { 
      id: 102, section: 'Verbal Ability (VARC)', type: 'CAT', class: '8', div: 'B', date: '31/03/2026', time: '12:30', duration: '40 min', topic: 'GIT',
      question: "Which core skill does the VARC section primarily evaluate?",
      options: [
        { letter: 'A', text: "Data Interpretation" },
        { letter: 'B', text: "Critical Reading and Grammar" }
      ]
    }
  ]);

  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);

  // --- HANDLERS (BASE LOGIC) ---
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to remove this test?")) {
      setTests(tests.filter(t => t.id !== id));
    }
  };

  const handleEditClick = (test) => {
    setEditingId(test.id);
    setEditFormData({ ...test });
  };

  const handleUpdate = () => {
    const updatedTests = tests.map(t => (t.id === editingId ? editFormData : t));
    setTests(updatedTests);
    if (selectedTest && selectedTest.id === editingId) {
      setSelectedTest(editFormData);
    }
    setEditingId(null);
  };

  const handleViewPreview = (test) => {
    setSelectedTest(test);
    setShowFullPreview(true);
  };

  // Logic to update question/options inside editFormData
  const handleQuestionChange = (val) => {
    setEditFormData(prev => ({ ...prev, question: val }));
  };

  const handleOptionChange = (index, val) => {
    const newOptions = [...editFormData.options];
    newOptions[index].text = val;
    setEditFormData(prev => ({ ...prev, options: newOptions }));
  };

  return (
    <section className="preview-viewport ff1 no-scroll">
      <div className="preview-container">
        <header className="preview-header">
          <div className="title-area">
            <h2>Generated Tests Preview</h2>
            <p>Review and manage section-wise tests before publishing.</p>
          </div>
          <div className="stats-badge ff2">{tests.length} Active Tests</div>
        </header>

        <div className="test-list-wrapper">
          {tests.map((test) => {
            const isEditing = editingId === test.id;
            return (
              <div key={test.id} className={`test-action-card ${isEditing ? 'is-editing' : ''}`}>
                <div className="info-side">
                  <div className="icon-box"><BookOpen size={20} color="#34a853" /></div>
                  <div className="text-box">
                    <h3>{test.section}</h3>
                    <span className="sub-label ff2">{test.topic}</span>
                  </div>
                </div>
                <div className="v-divider" />
                <div className="details-side ff2">
                  {['class', 'div', 'date', 'time', 'duration'].map((field) => (
                    <div className="meta-item" key={field}>
                      <label>{field.toUpperCase()}</label>
                      {isEditing ? (
                        <input 
                          value={editFormData[field]} 
                          onChange={(e) => setEditFormData({...editFormData, [field]: e.target.value})} 
                          className="edit-input" 
                        />
                      ) : <p>{test[field]}</p>}
                    </div>
                  ))}
                </div>
                <div className="actions-side">
                  {isEditing ? (
                    <div className="edit-actions">
                      <button className="confirm-btn" onClick={handleUpdate}><Check size={14} /> Update</button>
                      <button className="cancel-icon-btn" onClick={() => setEditingId(null)}><X size={16} /></button>
                    </div>
                  ) : (
                    <>
                      <button className="btn-icon view" onClick={() => handleViewPreview(test)}><Eye size={18} /></button>
                      <button className="btn-icon edit" onClick={() => handleEditClick(test)}><Edit3 size={18} /></button>
                      <button className="btn-icon delete" onClick={() => handleDelete(test.id)}><Trash2 size={18} /></button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <footer className="preview-footer-actions">
          <button className="cancel-reset-link" onClick={onCancel}>Cancel & Reset</button>
          <button className="finalize-btn" onClick={() => alert("Tests Finalized!")} disabled={editingId !== null}>
            Finalize All Tests
          </button>
        </footer>
      </div>

      {/* --- MODAL WITH INTEGRATED EDIT/UPDATE LOGIC --- */}
      {showFullPreview && (
        <div className="assessment-modal-overlay">
          <div className="assessment-modal">
            <div className="modal-header">
              <div className="header-titles">
                <h2>Assessment Preview</h2>
                <p>Reviewing generated paper structure</p>
              </div>
              <div className="header-actions-group">
                {editingId === selectedTest.id ? (
                  <button className="modal-update-btn ff1" onClick={handleUpdate}>
                    <Check size={16} /> Save Changes
                  </button>
                ) : (
                  <button className="modal-edit-btn ff1" onClick={() => handleEditClick(selectedTest)}>
                    <Edit3 size={16} /> Edit Question
                  </button>
                )}
                <button className="close-x" onClick={() => {setShowFullPreview(false); setEditingId(null);}}><X size={24} /></button>
              </div>
            </div>

            <div className="metadata-bar ff2">
              <div className="meta-left">
                <span>Subject: <strong>{selectedTest.section}</strong></span>
                <span>Topic: <strong>{selectedTest.topic}</strong></span>
                <span>Total Qs: <strong style={{color: '#63b3ed'}}>6</strong></span>
              </div>
              <div className="meta-right"><span className="draft-badge">Draft Mode</span></div>
            </div>

            <div className="modal-content-area">
              <div className="question-paper-card">
                <div className="section-header ff2">
                  <span>SECTION A: MULTIPLE CHOICE QUESTIONS</span>
                  <span className="marks">(1 Mark each)</span>
                </div>

                <div className="question-edit-box">
                  {editingId === selectedTest.id ? (
                    <textarea 
                      className="edit-textarea-ques ff1"
                      value={editFormData.question}
                      onChange={(e) => handleQuestionChange(e.target.value)}
                    />
                  ) : (
                    <div className="question-text">{selectedTest.question}</div>
                  )}
                </div>

                <div className="options-grid">
                  {(editingId === selectedTest.id ? editFormData.options : selectedTest.options).map((opt, idx) => (
                    <div className="option-box" key={opt.letter}>
                      <span className="opt-letter">{opt.letter}</span>
                      {editingId === selectedTest.id ? (
                        <textarea 
                          className="edit-textarea-opt ff2"
                          value={opt.text}
                          onChange={(e) => handleOptionChange(idx, e.target.value)}
                        />
                      ) : <p>{opt.text}</p>}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="close-preview-btn ff1" onClick={() => {setShowFullPreview(false); setEditingId(null);}}>Close Preview</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default TestPreview;